import React from 'react';
import { PaginationData } from '../types';
import Button from './UC/Button';
import styles from './Pagination.module.css';

interface PaginationProps {
  pagination: PaginationData;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  disabled?: boolean;
  pageSizeOptions?: number[];
  /**
   * Optional formatter for the range/total summary. Receives the first/last item
   * indexes and the total count. Defaults to the Spanish
   * "Mostrando {start}-{end} de {total} elementos".
   */
  infoLabel?: (start: number, end: number, total: number) => string;
  /** Label preceding the page-size selector. Defaults to the Spanish "Elementos por página:". */
  pageSizeLabel?: string;
  /** Label for the "previous page" button. Defaults to the Spanish "Anterior". */
  previousLabel?: string;
  /** Label for the "next page" button. Defaults to the Spanish "Siguiente". */
  nextLabel?: string;
}

const Pagination: React.FC<PaginationProps> = ({
  pagination,
  onPageChange,
  onPageSizeChange,
  disabled = false,
  pageSizeOptions = [10],
  infoLabel,
  pageSizeLabel = 'Elementos por página:',
  previousLabel = 'Anterior',
  nextLabel = 'Siguiente',
}) => {
  const {
    page: currentPage,
    totalPages,
    total: totalCount,
    limit: pageSize,
  } = pagination;

  const MIN_PAGE_SIZE = pageSizeOptions[0]; // Minimum page size option

  if (onPageSizeChange) {
    // Don't show pagination if total items are less than or equal to minimum page size
    if (totalCount <= MIN_PAGE_SIZE) {
      return null;
    }
  } else {
    // Don't show pagination if total items are less than or equal to minimum page size
    if (totalPages <= 1) {
      return null;
    }
  }

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const handlePageClick = (page: number) => {
    if (page !== currentPage) {
      onPageChange(page);
    }
  };

  const handlePageSizeChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const newPageSize = parseInt(event.target.value, MIN_PAGE_SIZE);
    if (onPageSizeChange) {
      onPageSizeChange(newPageSize);
    }
  };

  // Generate page numbers to show
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first page
      pages.push(1);

      if (currentPage > 3) {
        pages.push('...');
      }

      // Show current page and neighbors
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        if (i !== 1 && i !== totalPages) {
          pages.push(i);
        }
      }

      if (currentPage < totalPages - 2) {
        pages.push('...');
      }

      // Show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  const infoText = infoLabel
    ? infoLabel(startItem, endItem, totalCount)
    : `Mostrando ${startItem}-${endItem} de ${totalCount} elementos`;

  return (
    <div className={styles.pagination}>
      <div className={styles.info}>{infoText}</div>

      {onPageSizeChange && (
        <div className={styles.pageSizeSelector}>
          <label htmlFor="pageSize">{pageSizeLabel}</label>
          <select
            id="pageSize"
            value={pageSize}
            onChange={handlePageSizeChange}
            disabled={disabled}
            className={styles.pageSizeSelect}
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
      )}

      {totalPages > 1 && (
        <div className={styles.controls}>
          <Button
            variant="secondary"
            size="sm"
            onClick={handlePrevious}
            disabled={disabled || currentPage === 1}
          >
            {previousLabel}
          </Button>

          <div className={styles.pages}>
            {pageNumbers.map((page, index) =>
              page === '...' ? (
                <span key={`ellipsis-${index}`} className={styles.ellipsis}>
                  ...
                </span>
              ) : (
                <button
                  key={page}
                  className={`${styles.pageButton} ${
                    page === currentPage ? styles.active : ''
                  }`}
                  onClick={() => handlePageClick(page as number)}
                  disabled={disabled}
                >
                  {page}
                </button>
              )
            )}
          </div>

          <Button
            variant="secondary"
            size="sm"
            onClick={handleNext}
            disabled={disabled || currentPage === totalPages}
          >
            {nextLabel}
          </Button>
        </div>
      )}
    </div>
  );
};

export default Pagination;
